import React from 'react'

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'

import { v4 as uniqid } from 'uuid'

import { MdDeleteForever } from 'react-icons/md'

import getFormStatus from '@/Form/Functions/getFormStatus'

import ModelPicker from '@/Form/V2/ModelPicker'
import Input       from '@/Form/V2/Input'

import Loader from '@Pure/Loader'

import request from '@function/ajax/request'

import { num, formatNum } from '@root/utility'

import FormsStore from '@Store/Forms'

const header =
[
	{
		value: 'title',
		label: 'Désignation'
	},
	{
		value: 'price_ref',
		label: 'Ref',
		credit_note: true
	},
	{
		value: 'accounting_code',
		label: 'Code compta'
	},
	{
		value: 'amount',
		label: 'Montant',
		type: ['member', 'EC', 'partner']
	},
	{
		value: 'bill_amount',
		label: 'Prix unitaire',
		type: ['on_bill']
	},
	{
		value: 'qty',
		label: 'Quantité'
	},
	{
		value: 'discount',
		label: 'Remise (%)',
		bill: true
	},
	{
		value: 'bill_qty',
		label: 'Qté facturée',
		type: ['on_bill']
	},
	{
		value: 'bill_total',
		label: 'Facture',
		type: ['on_bill']
	},
	{
		value: 'total',
		label: 'Avoir',
		credit_note: true
	},
	{
		value: 'total',
		label: 'Facture',
		bill: true
	},
	{
		value: 'delete',
		label: '',
		type: ['EC', 'member', 'partner']
	}
];

const cell_class = 'c-bill-rows__cell BillRow-';

export default function BillRows ({ bill_id, mode, ...props })
{
	const [status, setStatus] = useState({ });
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(bill_id ? true : false);

	const state_type = FormsStore.useState(s => s?.[props.form]?.type);

	const type = props.mode === 'credit_note' ? 'on_bill' : state_type;

	const _ref_update = useRef();

	const onDelete = useCallback(function ({ index, id })
	{
	// avoir sur facture, lignes non supprimables
		if (props.bill_number) return;

		setRows(function (rows)
		{
			const last_i = rows.length-1;

			_ref_update.current = function ()
			{
				deleteRowFromStore(
				{
					FORM_NAME: props.form,
					last_i: last_i,
					index: index
				});
			};

			return rows.slice(0, -1);
		});
	},
	[props.bill_number, props.form]);

	useEffect(function ()
	{
		if (!bill_id) return;

		const controller = new AbortController();

		(async function ()
		{
			setLoading(true);

			try
			{
				var response = await request('/bills/get-one')
				.config({ controller, keyword: 'action' })
				.post({ id: bill_id });
			}
			catch (update)
			{
				if (controller?.signal?.aborted) return;

				setLoading(false);

				return setStatus(update);
			}

			const bill = response.bill || { };

			// const { shippings, order_has_attachments } = response;

			setRows(function (rows)
			{
				const new_rows = [];

			// pour supprimer les anciennes lignes s'il y en a plus que
			// pour la nouvelle facture
				const rows_count = rows.length;

				const store_update = { };

				(bill.rows || []).forEach(function (row, i)
				{
					const pattern = 'rows[' + i + '][';

					const id = row.id || row.row_type;

					const set_amount = num(row.amount) * -1;

					store_update[pattern + 'title]'] = row.title;
					store_update[pattern + 'id]'] = id;
					store_update[pattern + 'qty]'] = '';
					store_update[pattern + 'bill_qty]'] = String(num(row.qty, 0));
					store_update[pattern + 'amount]'] = String(set_amount);
					store_update[pattern + 'accounting_code]'] = row.accounting_code ||'';
					store_update[pattern + 'price_ref]'] = row.price_ref || '';

					store_update[pattern + 'bill_total]'] = String(
						num(set_amount * -1 * num(row.qty, 0))
					);

					new_rows.push(row.id);
				});

				_ref_update.current = function ()
				{
					deletePreviousStoreRows(
					{
						FORM_NAME: props.form,
						store_update: store_update,
						rows_count: rows_count,
						bill_rows_count: bill.rows.length
					});
				};

				return new_rows;
			});

			setLoading(false);
		})();

		return () => controller.abort?.();
	},
	[bill_id, props.form]);

	useEffect(function ()
	{
		if (typeof _ref_update.current !== 'function') return;

		_ref_update.current();

		_ref_update.current = undefined;
	},
	[rows]);

	const bill_rows = rows.map(function (id, i)
	{
		return (
			<Row key={ id } form={ props.form }
				status={ props.status }
				index={ i } id={ id }
				onDelete={ onDelete }
				deletable={ bill_id ? false : true }
				bill_id={ bill_id }
				type={ type }
				mode={ mode }
			/>
		);
	});

	const effective_header = useMemo(function ()
	{
		return header.filter(function (opt)
		{
			if (opt.type && !opt.type.includes(type)) return false;

			if (opt.credit_note) return mode === 'credit_note';
			if (opt.bill) return mode === 'bill';

			return true;
		});
	},
	[type, mode]);

	const header_cells = effective_header.map(function (opt)
	{
		return (
			<div className={ cell_class + opt.value }
				key={ opt.value }>
				{ opt.label }
			</div>
		);
	});

	const addRow = useCallback(function (e)
	{
		const id = uniqid();

		setRows(function (rows)
		{
			FormsStore.update(function (store)
			{
				store[props.form] ||= { };

				store[props.form]['rows[' + rows.length + '][qty]'] = '1';
			});

			return rows.concat([id]);
		});
	},
	[props.form]);

	const pickService = useCallback(function ({ value, data })
	{
		if (!data) return;

		const id = uniqid();

		const code = data.accounting_code || '';
		const title = data.title || '';
		const amount = data.amount || '';

		setRows(function (rows)
		{
			FormsStore.update(function (store)
			{
				store[props.form] ||= { };

				store[props.form]['rows[' + rows.length + '][qty]'] = '1';

				store[props.form]['rows[' + rows.length + '][title]'] = title;
				store[props.form]['rows[' + rows.length + '][amount]'] = amount;
				store[props.form]['rows[' + rows.length + '][accounting_code]'] = code;
			});

			return rows.concat([id]);
		});
	},
	[props.form]);

	return (
		<div className="c-bill-rows">

			<div className="c-bill-rows__header">
				{ header_cells }
			</div>

			<div className="c-bill-rows__list">
				{ bill_rows }
			</div>

			{
				rows.length > 1 ?
				(
					<div className="c-bill-rows__totals">
						<Totals FORM_NAME={ props.form } header={ effective_header }
							type={ type }
							mode={ mode }
						/>
					</div>
				)
				: undefined
			}

			{ getFormStatus(status) }

			<div className="c-bill-rows__buttons">

				{
					!bill_id ?
					(
						<button type="button" onClick={ addRow }
							className="c-btn c-btn--add">
							Nouvelle ligne
						</button>
					)
					: undefined
				}

				{
					!bill_id && props.mode !== 'credit_note' ?
					(
						<div className="c-bill-rows__add-service">
							<ModelPicker name="service" key="service"
								label="Ajouter via un service"
								model="service"
								bemit={ ['50%'] }
								url="/autocomplete/services"
								onChange={ pickService }
							/>
						</div>
					)
					: undefined
				}

			</div>

			{
				loading ?
				(
					<Loader bemit={ ['bg-white', 'primary'] }
						className="c-loader"
					/>
				)
				: undefined
			}

		</div>
	);
}

// eslint-disable-next-line react/no-multi-comp
function Row ({ form, onDelete, deletable, bill_id, type, mode, ...props })
{
	const errors = props.status?.fields || { };

	const pattern = 'rows[' + props.index + '][';

	const total_price = FormsStore.useState(function (store)
	{
		const discount = num(store[form][pattern + 'discount]']);
		const amount   = num(store[form][pattern + 'amount]']);
		const qty      = num(store[form][pattern + 'qty]']);

		const discounted = discount > 0
		? num(amount * discount / 100)
		: 0;

		return num(num(amount - discounted) * qty);
	});

	const bill_infos = FormsStore.useState(function (store)
	{
		const bill_total = num(store[form][pattern + 'bill_total]']);
		const amount = num(store[form][pattern + 'amount]']);
		const qty = num(store[form][pattern + 'bill_qty]']);

		const price_ref = store[form][pattern + 'price_ref]'];

		const obj =
		{
			total: bill_total,
			amount: amount,
			qty: qty,
			price_ref: price_ref
		};

		return obj;
	});

	const deleteRow = useCallback(function (e)
	{
		onDelete({ index: props.index, id: props.id });
	},
	[props.index, onDelete, props.id]);

	return (
		<div className="c-bill-rows__row">

			<div className={ cell_class + 'title' }>
				<Input name={ pattern + 'title]' }
					form={ form }
					bemit={ ['100%'] }
					error={ errors[pattern + 'title]']?.error }
				/>
			</div>

			{
				bill_id && type === 'on_bill' ?
				(
					<div className={ cell_class + 'price_ref' }>
						{ bill_infos.price_ref }
					</div>
				)
				: undefined
			}

			<div className={ cell_class + 'accounting_code' }>
				<Input name={ pattern + 'accounting_code]' }
					form={ form }
					bemit={ ['100%', 'align-right'] }
					error={ errors[pattern + 'accounting_code]']?.error }
					safe-number
				/>
			</div>

			{
				bill_id ? undefined :
				(
					<div className={ cell_class + 'amount' }>
						<Input name={ pattern + 'amount]' }
							form={ form }
							bemit={ ['100%', 'align-right'] }
							error={ errors[pattern + 'amount]']?.error }
							safe-number
						/>
					</div>
				)
			}

			{
				bill_id ?
				(
					<div className={ cell_class + 'bill_amount' }>

						{
							mode === 'credit_note'
							? formatNum(num(bill_infos.amount) * -1, '0,0[.]00')
							: formatNum(bill_infos.amount, '0,0[.]00')
						}
						&nbsp;€
					</div>
				)
				: undefined
			}

			<div className={ cell_class + 'qty' }>
				<Input name={ pattern + 'qty]' }
					form={ form }
					bemit={ ['100%', 'align-right'] }
					error={ errors[pattern + 'qty]']?.error }
					safe-number
				/>
			</div>

			{
				mode !== 'credit_note' ?
				(
					<div className={ cell_class + 'discount' }>
						<Input name={ pattern + 'discount]' }
							form={ form }
							bemit={ ['100%', 'align-right'] }
							error={ errors[pattern + 'discount]']?.error }
							safe-number
						/>
					</div>
				)
				: undefined
			}

			{
				type === 'on_bill' ?
				(
					<div className={ cell_class + 'bill_qty' }>
						{ formatNum(bill_infos.qty, '0,0[.]00') }
					</div>
				)
				: undefined
			}

			{
				type === 'on_bill' ?
				(
					<div className={ cell_class + 'bill_total' }>
						{ formatNum(bill_infos.total, '0,0[.]00') }
						&nbsp;€
					</div>
				)
				: undefined
			}

			<div className={ cell_class + 'total' }>
				{ formatNum(total_price, '0,0[.]00') }
				&nbsp;€
			</div>

			{
				deletable ?
				(
					<div className={ cell_class + 'delete' }>
						<button type="button" onClick={ deleteRow }
							className="c-btn c-btn--in-cell c-btn--delete-row">
							<MdDeleteForever className="c-btn__icon" />
							<span className="u-readers-only">Supprimer</span>
						</button>
					</div>
				)
				: undefined
			}

		</div>
	);
}

function deleteRowFromStore ({ last_i, FORM_NAME, index })
{
// recalibre les valeurs qui sont en rows[0][amount] rows[4][amount] etc
	FormsStore.update(function (store)
	{
		store[FORM_NAME] ||= { };

		const update = { };

	// on descend les valeurs après la ligne supprimée
	// si c'est la dernière ligne qui est supprimée, on ignore cette partie
		if (index < last_i && last_i > 0)
		{
			Array.from({ length: last_i - index })
			.forEach(function (_v, i)
			{
				header.forEach(function (opt)
				{
					const copy_to_i = 'rows[' + (i+index) + ']' +
						'[' + opt.value + ']';

					const copy_from_i = 'rows[' + (i+index+1) + ']' +
						'[' + opt.value + ']';

					if (store[FORM_NAME][copy_from_i] !== undefined)
					{
						update[copy_to_i] = store[FORM_NAME][copy_from_i];
					}
				});
			});
		}

		Object.assign(store[FORM_NAME], update);

	// on supprime la dernière ligne qui est maintenant vide
	// (descendue ou supprimée)
		header.forEach(function (opt)
		{
			const pattern = 'rows[' + last_i + '][' + opt.value + ']';

			delete store[FORM_NAME][pattern];
		});
	});
}

function deletePreviousStoreRows ({ FORM_NAME, rows_count, bill_rows_count,
	store_update })
{
	FormsStore.update(function (store)
	{
		store[FORM_NAME] ||= { };

		Object.assign(store[FORM_NAME], store_update);

		if (rows_count > bill_rows_count)
		{
			Array.from({ length: rows_count - bill_rows_count })
			.forEach(function (_v, i)
			{
				const index = bill_rows_count + i;

				header.forEach(function (opt)
				{
					const pattern = 'rows[' + index + '][' +
						opt.value + ']';

					delete store[FORM_NAME][pattern];
				});
			});
		}
	});
}

function Totals ({ type, mode, FORM_NAME, header })
{
	const totals = FormsStore.useState(function (store)
	{
		const totals = { };

		const data = store[FORM_NAME] || { };

		Object.keys(data)
		.filter(key => key.startsWith('rows[') && key.endsWith('][qty]'))
		.forEach(function (qty_key)
		{
			let index = qty_key.replace('rows[', '');
			index = index.slice(0, index.indexOf(']'));

			const pattern = 'rows[' + index + '][';

			const row_amount = num(data[pattern + 'amount]']);

			totals['amount'] = num(num(totals['amount']) + row_amount);

			const row_total =  num(
				row_amount * num(data[pattern + 'qty]'], 0)
			);

			totals['total'] = num(
				num(totals['total']) + num(row_total)
			);

			totals['bill_total'] = num(
				num(totals['bill_total']) + num(data[pattern + 'bill_total]'])
			);
		});

		return totals;
	});

	const totals_cells = header.map(function (opt)
	{
		let class_name = cell_class + opt.value;

		let value = undefined;

		if (opt.value === 'amount' || opt.value === 'bill_amount'
			&& type !== 'on_bill')
		{
			value = totals.amount > 0 && mode === 'credit_note' ? '-' : '';
			value += formatNum(totals.amount, '0,0[.]00') + ' €';
		}
		else if (opt.value === 'total')
		{
			value = totals.total > 0 && mode === 'credit_note' ? '-' : '';
			value += formatNum(totals.total, '0,0[.]00') + ' €';
		}
		else if (opt.value === 'bill_total')
		{
			value = formatNum(totals.bill_total, '0,0[.]00') + ' €';
		}

		return (
			<div className={ class_name }
				key={ opt.value }>
				{ value }
			</div>
		);
	});

	return totals_cells;
}